'use client'

import { cn } from '@/utilities/cn'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { Props as MediaProps } from '../types'

export const Video: React.FC<MediaProps> = (props) => {
  const { className, onClick, resource, videoClassName } = props
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isReady, setIsReady] = useState(false)

  const src = useMemo(() => {
    if (resource && typeof resource === 'object') {
      const { filename, url } = resource
      return url || `${process.env.NEXT_PUBLIC_SERVER_URL}/media/${filename}`
    }
    return ''
  }, [resource])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !src) return

    setIsReady(false)

    // Muted before any event so the autoplay policy allows playback; users can
    // still unmute via the native controls.
    video.defaultMuted = true
    video.muted = true

    const markReady = () => setIsReady(true)
    const onCanPlay = () => {
      setIsReady(true)
      video.play().catch(() => {
        // autoplay blocked, user can use native controls
      })
    }

    if (video.readyState >= 1) markReady()
    if (video.readyState >= 3) onCanPlay()

    video.addEventListener('loadedmetadata', markReady)
    video.addEventListener('loadeddata', markReady)
    video.addEventListener('canplay', onCanPlay)
    video.addEventListener('error', markReady)

    return () => {
      video.removeEventListener('loadedmetadata', markReady)
      video.removeEventListener('loadeddata', markReady)
      video.removeEventListener('canplay', onCanPlay)
      video.removeEventListener('error', markReady)
    }
  }, [src])

  if (!src) return null

  return (
    <div className={cn('relative h-full w-full', className)}>
      <video
        autoPlay
        className={cn('h-full w-full', videoClassName)}
        controls
        loop
        onClick={onClick}
        playsInline
        preload="metadata"
        key={src}
        muted
        ref={videoRef}
        src={src}
      />

      {!isReady && <div className="pointer-events-none absolute inset-0 animate-pulse bg-black/10" />}
    </div>
  )
}
